import { keyBy, uniqBy } from 'lodash';
import moment, { duration as momentDuration } from 'moment';
import {
  AllocationMedium,
  AllocationSourceType,
  ResourceType,
} from 'api/resource-management/types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import {
  ProjectTemplateTask,
  TemplateAllocation,
} from 'data/templates/projects';
import { floatUid } from 'utils/uid';
import { hoursPerWeekModifier } from '../managers/hours-per-week-modifier';
import { TemplateAssignee } from '../types';
import { asAllocationEvent } from './as-allocation-event';
import { asTemplateAllocation } from './as-template-allocation';
import { filterUncreatedTasks } from './assignee-data';
import { mergeAllocationsForAssignee } from './merge-allocations';
import { AllWorkingDayConfig } from './working-days';

const one_minute = momentDuration(1, 'minute').asMinutes();

export const autoAllocateFromTasks = (
  tasks: ProjectTemplateTask[],
  isHoursPerWeek?: boolean,
): TemplateAllocation[] =>
  tasks.reduce(
    (allocations, { effort, assignee, startDay, duration, taskId }) => {
      const assigneeCount =
        (assignee?.teams ?? []).filter((x) => x.teamType !== 'EXTERNAL')
          .length + (assignee?.users ?? []).length;

      if (
        effort == null ||
        effort === 0 ||
        assignee == null ||
        assigneeCount === 0
      ) {
        return allocations;
      }

      // if (isHoursPerWeek) {
      //   if (startDay % 5 !== 1) {
      //     do {
      //       startDay -= 1;
      //       duration += 1;
      //     } while (startDay % 5 !== 1);
      //   }

      //   if (duration % 5 !== 0) {
      //     do {
      //       duration += 1;
      //     } while (duration % 5 !== 0);
      //   }
      // }

      const minutes = Math.floor(
        Math.max(effort / assigneeCount / duration, one_minute),
      );
      const commonProps = {
        startDay,
        duration,
        minutes,
        billable: true,
        medium: AllocationMedium.Hard,
        sources: [
          {
            sourceType: AllocationSourceType.Task,
            sourceId: taskId,
          },
        ],
      };

      const processedAllocations = allocations
        .concat(
          assignee.teams
            .filter((x) => x.teamType !== 'EXTERNAL')
            .map((x) => ({
              id: floatUid(),
              ...commonProps,
              type: ResourceType.Team,
              teamId: x.teamId,
            })),
        )
        .concat(
          assignee.users.map((x) => ({
            id: floatUid(),
            ...commonProps,
            type: ResourceType.User,
            userId: x.userId,
          })),
        );

      if (isHoursPerWeek) {
        return hoursPerWeekModifier(processedAllocations);
      }

      return processedAllocations;
    },
    [] as TemplateAllocation[],
  );

export const getAutoAllocatedAssignees = (
  allTasks: ProjectTemplateTask[],
): TemplateAssignee =>
  filterUncreatedTasks(allTasks).reduce(
    (assignees, task) => {
      const { effort, assignee } = task;
      const assigneeCount =
        (assignee?.teams ?? []).length + (assignee?.users ?? []).length;

      if (
        effort == null ||
        effort === 0 ||
        assignee == null ||
        assigneeCount === 0
      ) {
        return assignees;
      }

      return {
        users: uniqBy(
          assignees.users.concat(task.assignee?.users ?? []),
          'userId',
        ),
        teams: uniqBy(
          assignees.teams.concat(task.assignee?.teams ?? []),
          'teamId',
        ),
      };
    },
    { users: [], teams: [] } as TemplateAssignee,
  );

export const getAutoAllocatedAllocationsFromTasks = (
  tasks: ProjectTemplateTask[],
): TemplateAllocation[] => {
  const rawAllocations = autoAllocateFromTasks(tasks);
  const allocatedMembers = getAutoAllocatedAssignees(tasks);
  const userLookup = keyBy(allocatedMembers.users, 'userId');
  const teamLookup = keyBy(allocatedMembers.teams, 'teamId');
  const relativeDate = moment().startOf('day');
  const allocationEvents = asAllocationEvent({
    relativeDate,
    userLookup,
    teamLookup,
    projectId: floatUid(),
    projectPhaseLookup: {},
    workingDaysConfig: AllWorkingDayConfig,
  })(rawAllocations);

  return mergeAllocationsForAssignee(
    allocatedMembers,
    AllWorkingDayConfig,
  )(allocationEvents).map((x) =>
    asTemplateAllocation(relativeDate)(
      asAllocationPayload(new AllocationEventModel(x)),
    ),
  );
};
